import React from "react";
import {
  ArrowRight,
  Sparkles,
  Compass,
  Users,
  Globe,
  HeartHandshake,
  Linkedin,
  Twitter,
} from "lucide-react";
import Navbar from "@/components/stori/Navbar";
import Footer from "@/components/stori/Footer";
import SectionHeading from "@/components/stori/SectionHeading";
import { LeadModalProvider, useLeadModal } from "@/components/stori/LeadModalContext";
import LeadFormModal from "@/components/stori/LeadFormModal";
import Seo from "@/seo/Seo";

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
function AboutHero() {
  return (
    <section
      data-testid="about-hero"
      className="relative pt-36 pb-20 lg:pt-44 lg:pb-28 border-b border-stori-border overflow-hidden"
    >
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-stori-cyan/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute inset-0 grid-noise opacity-30 radial-mask pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-stori-cyan flex items-center gap-2 mb-6">
            <span className="inline-block h-px w-10 bg-stori-cyan" />
            ABOUT STORI
          </div>
          <h1
            data-testid="about-h1"
            className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white"
          >
            <span className="block fade-up">We turn moments into</span>
            <span
              className="block fade-up text-stori-cyan cyan-text-glow"
              style={{ animationDelay: "120ms" }}
            >
              measurable marketing.
            </span>
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-stori-muted max-w-2xl leading-relaxed">
            Stori is the AI engagement layer for the world's most beautiful spaces.
            Built by hospitality operators and AI researchers, we turn fleeting guest
            moments into compliant first-party data, automated playbooks, and
            attributable revenue.
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Mission
// ---------------------------------------------------------------------------
function Mission() {
  return (
    <section
      data-testid="about-mission"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-8 bg-stori-cyan" />
          OUR MISSION
          <span className="inline-block h-px w-8 bg-stori-cyan" />
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.15]">
          Every space — a hotel lobby, a flagship store, a sold-out activation —
          should know{" "}
          <span className="text-stori-cyan cyan-text-glow">
            every guest who walks in,
          </span>{" "}
          and treat them like it.
        </h2>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Story
// ---------------------------------------------------------------------------
function Story() {
  return (
    <section
      data-testid="about-story"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative rounded-2xl overflow-hidden border border-stori-border">
          <img
            src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1600&q=80"
            alt="Stori activation in a luxury hotel lobby"
            loading="lazy"
            className="w-full h-full object-cover aspect-[4/5]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stori-bg/40 to-transparent pointer-events-none" />
          <div className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.22em] text-white">
            · Aurelia Hotel Group · Singapore lobby ·
          </div>
        </div>

        <div>
          <div className="label-eyebrow text-stori-cyan flex items-center gap-2 mb-5">
            <span className="inline-block h-px w-8 bg-stori-cyan" />
            OUR STORY
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.1]">
            Built in hotel lobbies. Trained on real guests.
          </h2>
          <div className="mt-8 space-y-5 text-stori-muted leading-relaxed text-base lg:text-lg">
            <p>
              Stori started in 2012 with a single ring light at a celebrity birthday
              party. Within a week, the same hardware was running in a Beverly Hills
              flagship — and we saw what no one else did: every smile, every share,
              every walk-in was a marketing event nobody was capturing.
            </p>
            <p>
              We rebuilt the experience from the ground up. AI-powered photo
              experiences. Real-time influencer detection. Automated playbooks that
              fire within minutes of a guest leaving. Today, Stori powers
              installations across <span className="text-white">27 countries</span>,
              and our software handles{" "}
              <span className="text-white">25 million guest interactions</span> a
              year — without a single manual export.
            </p>
            <p>
              We're a small team of hospitality operators, AI researchers, and
              field engineers — headquartered in Los Angeles, working with operators
              from Tokyo to São Paulo. We exist to make every space remember every
              guest, and to turn that memory into revenue you can actually attribute.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------
const STATS = [
  { value: "25M+", label: "Guest interactions captured" },
  { value: "10K+", label: "Events & activations powered" },
  { value: "27", label: "Countries with live installs" },
  { value: "2012", label: "Year founded" },
];

function Stats() {
  return (
    <section
      data-testid="about-stats"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-surface1/40"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="BY THE NUMBERS"
          title="A decade of guest moments — measured."
          align="center"
        />
        <div className="mt-14 lg:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex flex-col gap-3 lg:border-l lg:border-stori-border lg:first:border-l-0 lg:pl-6 lg:first:pl-0"
            >
              <span className="font-heading text-4xl lg:text-6xl font-semibold text-white tracking-tight leading-none tabular-nums">
                {s.value}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-stori-muted">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------
const VALUES = [
  {
    icon: Compass,
    title: "Operator-first",
    desc: "We build for the GM, the marketing director, the event lead — people who run real venues with real numbers attached. If it doesn't ship revenue, we don't ship it.",
  },
  {
    icon: HeartHandshake,
    title: "Guest-respecting",
    desc: "Every interaction is consented, every dataset compliant, every photo deletable. We hold ourselves to GDPR, CCPA, and SOC2 Type II — even where regulation stops short.",
  },
  {
    icon: Sparkles,
    title: "Quietly luxurious",
    desc: "Our software runs in lobbies, ateliers, and Michelin dining rooms. The work is to feel inevitable — never invasive, never gimmicky, always on-brand for the space.",
  },
  {
    icon: Globe,
    title: "Field-tested everywhere",
    desc: "We don't ship features that haven't survived a Friday-night dinner rush in Singapore or a sold-out launch in São Paulo. The bar is real venues, real guests, real outcomes.",
  },
];

function Values() {
  return (
    <section
      data-testid="about-values"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="WHAT WE BELIEVE"
          title="Four principles. Every release."
          align="center"
        />
        <div className="mt-14 lg:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <article
                key={v.title}
                data-testid={`value-${i}`}
                className="p-7 rounded-2xl border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-colors flex flex-col"
              >
                <div className="w-11 h-11 rounded-lg border border-stori-border text-stori-cyan flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white">
                  {v.title}
                </h3>
                <p className="mt-3 text-sm text-stori-muted leading-relaxed">
                  {v.desc}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------
const TEAM = [
  {
    name: "Tamer Soliman",
    role: "Founder & CEO",
    bio: "Hospitality operator turned product founder. Previously led marketing for two boutique hotel groups across LA and Singapore.",
    image:
      "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Maya Chen",
    role: "Co-founder & CTO",
    bio: "Computer-vision researcher (ex-Stanford AI Lab). Architects the on-device ML stack that powers every Stori activation.",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Daniel Hahn",
    role: "Head of Hospitality",
    bio: "Former GM at Aurelia Singapore. Turns Stori playbooks into revenue inside hotels, restaurants, and flagship retail.",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Lucia Romero",
    role: "Head of Live Events",
    bio: "Ex-experiential producer. Runs the on-site team behind every Stori activation — Tokyo, NYC, São Paulo.",
    image:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=600&q=80",
  },
];

function Team() {
  return (
    <section
      data-testid="about-team"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-surface1/40"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="THE TEAM"
          title="Operators, engineers, and field producers."
          lead="A small team — under 40 — distributed across LA, Singapore, and London. We hire for taste, for craft, and for the patience to ship into rooms that don't tolerate jank."
          align="center"
        />
        <div className="mt-14 lg:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEAM.map((m, i) => (
            <article
              key={m.name}
              data-testid={`team-${i}`}
              className="rounded-2xl overflow-hidden border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-colors group"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-stori-bg">
                <img
                  src={m.image}
                  alt={m.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stori-bg via-stori-bg/30 to-transparent" />
                <div className="absolute bottom-4 right-4 flex items-center gap-2">
                  <a
                    href="#"
                    className="w-8 h-8 rounded-full border border-white/30 text-white/80 hover:border-stori-cyan hover:text-stori-cyan flex items-center justify-center transition-colors"
                    aria-label={`${m.name} on LinkedIn`}
                  >
                    <Linkedin className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="#"
                    className="w-8 h-8 rounded-full border border-white/30 text-white/80 hover:border-stori-cyan hover:text-stori-cyan flex items-center justify-center transition-colors"
                    aria-label={`${m.name} on Twitter`}
                  >
                    <Twitter className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-semibold text-white tracking-tight">
                  {m.name}
                </h3>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-stori-cyan">
                  {m.role}
                </div>
                <p className="mt-3 text-sm text-stori-muted leading-relaxed">
                  {m.bio}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 text-center">
          <p className="text-stori-muted text-sm">
            We're always hiring people who've actually run venues.{" "}
            <a
              href="mailto:meetstori@gmail.com"
              data-testid="careers-link"
              className="text-stori-cyan hover:underline font-medium inline-flex items-center gap-1"
            >
              See open roles
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Investors / Press
// ---------------------------------------------------------------------------
function Investors() {
  const items = [
    "Forbes",
    "TechCrunch",
    "Fast Company",
    "Hospitality Net",
    "Skift",
    "PSFK",
  ];
  return (
    <section
      data-testid="about-press"
      className="relative py-20 border-b border-stori-border"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stori-muted text-center mb-10">
          · Featured in ·
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
          {items.map((p) => (
            <span
              key={p}
              className="font-heading text-xl lg:text-2xl text-stori-muted/70 tracking-tight"
            >
              {p}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CTA
// ---------------------------------------------------------------------------
function AboutCTA() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="about-cta"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 grid-noise opacity-40 radial-mask pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-stori-cyan/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          WORK WITH US
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-[1.05]">
          Want Stori running in your space?
        </h2>
        <p className="mt-6 text-lg text-stori-muted max-w-2xl mx-auto leading-relaxed">
          Book a 20-minute walkthrough — live dashboards, real playbooks, and the
          ROI calculator we ship with every install.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="about-cta-demo"
            className="inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:-translate-y-0.5"
          >
            Book a demo
            <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href="mailto:meetstori@gmail.com"
            data-testid="about-cta-email"
            className="inline-flex items-center gap-2 text-stori-muted hover:text-white px-4 py-4"
          >
            <Users className="w-4 h-4" />
            Talk to the team directly
          </a>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page export
// ---------------------------------------------------------------------------
export default function About() {
  return (
    <LeadModalProvider>
      <Seo path="/about" />
      <main data-testid="about-page" className="bg-stori-bg text-white overflow-x-hidden">
        <Navbar />
        <AboutHero />
        <Mission />
        <Story />
        <Stats />
        <Values />
        <Team />
        <Investors />
        <AboutCTA />
        <Footer />
        <LeadFormModal />
      </main>
    </LeadModalProvider>
  );
}
